import { Box, Button, Modal } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Config } from "../../components/icons/Config";
import { ColorSettings } from "../settings/color-settings";
import { BranchesWhiteLabelService } from "../../services/branches-white-label-service";
import { useToast } from "../../context/toast-context";
import { useUser } from "../../context/user-context";

interface SettingsBranchProps {
  branchId: number;
  onlyRatings: boolean;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "700px",
  maxWidth: 1200,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

export function SettingsBranch({ branchId, onlyRatings }: SettingsBranchProps) {
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const { user } = useUser();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const branchesWhiteLabelService = new BranchesWhiteLabelService();

  
  const handleOpenModal = async () => {
    // Verificar si la marca blanca ya está activada
    await branchesWhiteLabelService.getWhiteLabelStatus(
      branchId,
              (data) => {
          // Verificar si está activada: debe tener fecha de inicio Y NO tener fecha de fin
          const isActivated = data.activatedStartDate && data.activatedEndDate === null;
          
          if (isActivated || data.activatedByAdmin) {
            // Si ya está activada, abrir directamente el modal de configuración
            setIsModalOpen(true);
        } else {
          // Si no está activada, mostrar el modal de confirmación de pago
          setIsPaymentModalOpen(true);
        }
      },
      (error) => {
        showToast(error, "error");
        setIsPaymentModalOpen(true);
      }
    );
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePaymentConfirm = async () => {
    
    if (!user?.userId) {
      showToast("Error: No se pudo obtener la información del usuario", "error");
      return;
    }

    setIsActivating(true);
    
    const currentDate = new Date().toISOString(); // Formato completo con hora YYYY-MM-DDTHH:mm:ss.sssZ
    
    const params = {
      branchId: branchId,
      activatedByManager: true,
      activatedByAdmin: false,
      activatedStartDate: currentDate,
      activatedEndDate: null,
      userId: user.userId,
    };
    
    await branchesWhiteLabelService.activateWhiteLabel(
      params,
      (data) => {
        showToast("Marca blanca activada exitosamente", "success");
        setIsPaymentModalOpen(false);
        setIsModalOpen(true);
        setIsActivating(false);
      },
      (error) => {
        showToast(error, "error");
        setIsActivating(false);
      }
    );
  };

  const handlePaymentCancel = () => {
    setIsPaymentModalOpen(false);
  };
  
  return (
    <>
      <Button
        title={t("branches.settings.title")}
        onClick={handleOpenModal}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
          padding: "6px",
        }}
      >
        <Config fill="#EF0BB8" />
      </Button>
      
      {/* Modal de confirmación de pago */}
      <Modal
        open={isPaymentModalOpen}
        onClose={handlePaymentCancel}
        aria-labelledby="payment-confirmation-modal"
        aria-describedby="Modal de confirmación de pago"
      >
        <Box sx={style}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
            <h1>¡Personaliza Divix con tu marca!</h1>
          </div>
          <div className="p-6">
            <p className="text-gray-600 text-[15px] leading-7 mb-6">
              Activando la edición de colores y logo se está aceptando el cobro mensual de USD 35 dólares más impuestos por sucursal.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="outlined"
                onClick={handlePaymentCancel}
                disabled={isActivating}
                sx={{
                  borderColor: "#898989",
                  color: "#898989",
                  textTransform: "none",
                  fontFamily: "Montserrat",
                  fontWeight: 300,
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handlePaymentConfirm}
                disabled={isActivating}
                sx={{
                  backgroundColor: "#EF0BB8",
                  "&:hover": {
                    backgroundColor: "#d009a6",
                  },
                  "&.Mui-disabled": {
                    opacity: "40%",
                    color: "#fff",
                  },
                  textTransform: "none",
                  fontFamily: "Montserrat",
                  fontWeight: 300,
                }}
              >
                {isActivating ? "Activando..." : "Aceptar"}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
      
      {/* Modal de configuración de marca blanca */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby={t("branches.settings.title")}
        aria-describedby={t("branches.settings.description")}
      >
        <Box sx={style} className="custom-scrollbar">
          <div className="bg-gray-light flex flex-col gap-2 text-center text-xl font-normal text-black py-8 px-2">
          <h1 className="font-montserrat font-medium">
                {t("branches.settings.title")}
              </h1>
          </div>
          <div className="p-6">
            <div className="w-full flex flex-col">
              <ColorSettings branchId={branchId} onClose={handleCloseModal} onlyRatings={onlyRatings}/>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}


